import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Subject, Subscription, auditTime } from 'rxjs';

import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

import type { HandoffRow } from '../../core/api/api.types';
import { HandoffApiService } from '../../core/api/handoff-api.service';
import { OperatorSocketService } from '../../core/socket/operator-socket.service';

@Component({
  selector: 'op-available-page',
  imports: [RouterModule, MatIconModule, MatProgressSpinnerModule, MatSnackBarModule],
  templateUrl: './inbox.page.html',
  styleUrl: './inbox.page.scss',
})
export class AvailablePage implements OnInit, OnDestroy {
  private readonly api = inject(HandoffApiService);
  private readonly socket = inject(OperatorSocketService);
  private readonly snack = inject(MatSnackBar);
  private readonly router = inject(Router);

  readonly handoffs = signal<HandoffRow[]>([]);
  readonly loading = signal(false);
  readonly busyId = signal<string | null>(null);
  readonly sortDirection = signal<'desc' | 'asc'>('desc');
  readonly sortedHandoffs = computed(() => {
    const direction = this.sortDirection();
    return [...this.handoffs()].sort((a, b) => {
      const aTime = new Date(a.created_at).getTime();
      const bTime = new Date(b.created_at).getTime();
      return direction === 'desc' ? bTime - aTime : aTime - bTime;
    });
  });

  /** Ticks every 30 s so overdue state updates without a page refresh. */
  readonly now = signal(Date.now());
  private tickInterval: ReturnType<typeof setInterval> | null = null;

  private readonly reload$ = new Subject<void>();
  private readonly sub = new Subscription();

  async ngOnInit(): Promise<void> {
    try {
      await this.socket.connect();
    } catch (err) {
      console.warn('[Available] WS connect failed', err);
    }

    this.sub.add(
      this.reload$.pipe(auditTime(250)).subscribe(() => {
        void this.loadAvailable();
      }),
    );

    this.sub.add(
      this.socket.handoffUpserted$.subscribe(() => {
        this.reload$.next();
      }),
    );

    this.sub.add(
      this.socket.handoffTaken$.subscribe(() => {
        this.reload$.next();
      }),
    );

    this.tickInterval = setInterval(() => this.now.set(Date.now()), 30_000);

    await this.loadAvailable();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    if (this.tickInterval !== null) clearInterval(this.tickInterval);
  }

  toggleTimeSort(): void {
    this.sortDirection.update((value) => (value === 'desc' ? 'asc' : 'desc'));
  }

  customerLabel(row: HandoffRow): string {
    if (row.customer_preferred_name) return row.customer_preferred_name;
    const source = (row.conversation_id || row.id || '').replace(/-/g, '').toUpperCase();
    const suffix = source.slice(-4);
    return suffix ? `Customer #${suffix}` : 'Customer';
  }

  private static readonly OVERDUE_MS = 5 * 60 * 1_000;

  isOverdue(row: HandoffRow): boolean {
    return this.now() - new Date(row.created_at).getTime() > AvailablePage.OVERDUE_MS;
  }

  formatCreatedAt(value: string): string {
    const createdAt = new Date(value);
    if (Number.isNaN(createdAt.getTime())) return '—';

    const diffMs = Math.max(0, Date.now() - createdAt.getTime());
    const totalMinutes = Math.floor(diffMs / 60000);

    if (totalMinutes < 1) return 'just now';
    if (totalMinutes < 60) return `${totalMinutes}m ago`;

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours < 24) {
      if (minutes > 0) return `${hours}h ${minutes}m ago`;
      return `${hours}h ago`;
    }

    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }

  async claim(row: HandoffRow): Promise<void> {
    this.busyId.set(row.id);
    try {
      await this.api.take(row.id);
      this.handoffs.update((items) => items.filter((item) => item.id !== row.id));
      this.snack.open('Handoff claimed', 'Dismiss', { duration: 2500 });
      void this.router.navigate(['/my-cases', row.id]);
    } catch (err: any) {
      console.error(err);
      // H2: another operator claimed it first. Drop the row locally with a clear
      // message instead of a generic error + full reload.
      const message = String(err?.message ?? '');
      if (err?.status === 409 || message.includes('handoff_not_pending')) {
        this.handoffs.update((items) => items.filter((item) => item.id !== row.id));
        this.snack.open('Already taken by another operator', 'Dismiss', { duration: 3000 });
      } else {
        this.snack.open(err?.message ?? 'Failed to claim handoff', 'Dismiss', { duration: 4000 });
        this.reload$.next();
      }
    } finally {
      this.busyId.set(null);
    }
  }

  private async loadAvailable(): Promise<void> {
    this.loading.set(true);

    try {
      const pageSize = 100;
      const firstPage = await this.api.inbox({ status: 'PENDING', page: 1, pageSize });
      const items = [...(firstPage.items ?? [])];
      const total = firstPage.total ?? items.length;
      const totalPages = Math.max(1, Math.ceil(total / pageSize));

      // M6: a failure on a later page must not discard the pages already fetched.
      let partial = false;
      for (let page = 2; page <= totalPages; page += 1) {
        try {
          const nextPage = await this.api.inbox({ status: 'PENDING', page, pageSize });
          items.push(...(nextPage.items ?? []));
        } catch (pageErr) {
          console.warn('[Available] partial load failure on page', page, pageErr);
          partial = true;
          break;
        }
      }

      this.handoffs.set(items);
      if (partial) {
        this.snack.open('Some requests could not be loaded. Showing a partial list.', 'Dismiss', {
          duration: 4000,
        });
      }
    } catch (err: any) {
      console.error(err);
      this.snack.open(err?.message ?? 'Failed to load available requests', 'Dismiss', {
        duration: 4000,
      });
    } finally {
      this.loading.set(false);
    }
  }
}